import React, { useState, useEffect, useCallback } from 'react'
import { Modal, message, Input, Row, Col, Checkbox, Button } from 'antd'
import {
  addPersonToFacility,
  assignPersonToExperts,
  getClients
} from '../../api'
import _ from 'lodash'
import styled from 'styled-components'
import { useImmer } from 'use-immer'
import { CharacterLevel } from '../../utils/constant'
import PageLoading from '../UI/status/loading'

const { Search } = Input

const StyledModal = styled(Modal)`
  .title {
    font-size: 36px;
    font-weight: 600;
  }

  .experts-list {
    max-height: 700px;
    overflow: auto;
    margin-top: 31px;
    margin-bottom: 20px;

    .avatar,
    .name {
      display: inline-block;
      vertical-align: middle;
      font-weight: 600;
      font-size: 16px;
    }
    .avatar {
      width: 32px;
      height: 32px;
      background: rgba(0, 0, 0, 0.1);
      border-radius: 100%;
      margin-right: 16px;
      overflow: hidden;
      img {
        max-width: 100%;
      }
    }
    .services {
      font-size: 14px;
      font-weight: 600;
      line-height: 32px;
      letter-spacing: 0.4px;
      color: rgba(0, 0, 0, 0.4);
      float: right;
    }
  }
`

export const FacilityModalAssignType = {
  addClient: 'Add Client',
  addExpert: 'Add Expert',
  selectExperts: 'Select Experts'
}

const filterClients = (clients, facilityId, characterLevel) =>
  clients.filter(
    (client) =>
      client.person_character === characterLevel &&
      !client.facility_ids.includes(facilityId)
  )

const FacilityAssignPersonModal = (props) => {
  const { facility, onCancel, confirmSync } = props

  const [assignType, setAssignType] = useState(props.assignModal)
  const [preFetching, setPreFetching] = useState(false)

  const [loading, setLoading] = useState(false)
  const [persons, setPersons] = useState(null)
  const [targetPersons, setTargetPersons] = useState([])
  const [filteredPersons, setFilteredPersons] = useState([])

  const [selectedClients, setSelectedClients] = useImmer([])
  const [selectedExperts, setSelectedExperts] = useImmer([])

  const addItemToList = (setter) => (item) => {
    setter((draft) => {
      draft.push(item)
    })
  }

  const deleteItemFromList = (setter) => (itemId) => {
    setter((draft) => {
      const index = draft.findIndex((item) => item.id === itemId)
      if (index > -1) {
        draft.splice(index, 1) // Remove the item from the array
      }
    })
  }

  const handleSelectClient = useCallback(addItemToList(setSelectedClients), [])
  const handleDeleteClient = useCallback(
    deleteItemFromList(setSelectedClients),
    []
  )

  const handleSelectExpert = useCallback(addItemToList(setSelectedExperts), [])
  const handleDeleteExpert = useCallback(
    deleteItemFromList(setSelectedExperts),
    []
  )

  useEffect(() => {
    const fetchAndFilterPersons = async () => {
      setPreFetching(true)
      try {
        const { clients } = await getClients()
        const characterLevel =
          assignType === FacilityModalAssignType.addClient
            ? CharacterLevel.client
            : CharacterLevel.expert
        const targets = filterClients(clients, facility.id, characterLevel)

        setPersons(clients)
        setTargetPersons(targets)
        setFilteredPersons(targets)
      } catch (err) {
        console.error('Failed to fetch and filter persons:', err)
      } finally {
        setPreFetching(false)
      }
    }

    if (!persons) {
      fetchAndFilterPersons()
    } else {
      const characterLevel =
        assignType === FacilityModalAssignType.addClient
          ? CharacterLevel.client
          : CharacterLevel.expert
      const targets = filterClients(persons, facility.id, characterLevel)

      setTargetPersons(targets)
      setFilteredPersons(targets)
    }
  }, [])

  const submit = async () => {
    if (assignType === FacilityModalAssignType.addClient) {
      setAssignType(FacilityModalAssignType.selectExperts)
      const targets = persons.filter(
        (client) =>
          client.person_character === CharacterLevel.expert &&
          client.facility_ids.includes(facility.id)
      )
      setTargetPersons(targets)
      setFilteredPersons(targets)
    } else {
      setLoading(true)
      try {
        if (assignType === FacilityModalAssignType.addExpert) {
          await addPersonToFacility({
            facility_id: facility.id,
            person_ids: selectedExperts.map((i) => i.id)
          })
          confirmSync(selectedClients, selectedExperts)
        } else {
          const group_experts = selectedExperts.map((i) => i.id)
          await assignPersonToExperts({
            facility_id: facility.id,
            client_ids: selectedClients.map((i) => i.id),
            assigned_ids: group_experts
          })
          const clients = selectedClients.map((client) => _.cloneDeep(client))
          clients.forEach((e) => {
            e.group_experts = group_experts
          })
          confirmSync(clients, selectedExperts)
        }
        message.success('Done')
      } catch (err) {
        console.error(err)
      } finally {
        setLoading(false)
      }
    }
  }

  const onSearch = (value, _e, info) => {
    const matchValue = value.toLowerCase()
    setFilteredPersons(
      _.filter(targetPersons, (person) => {
        const name = `${(person.profile && person.profile.nickname) ||
          person.first_name} ${person.last_name}`.toLowerCase()
        return name.includes(matchValue)
      })
    )
  }

  return (
    <StyledModal
      open={true}
      onCancel={onCancel}
      width={700}
      footer={
        <>
          <Button onClick={onCancel} ghost type="primary">
            CANCEL
          </Button>
          <Button onClick={submit} type="primary" loading={loading}>
            {assignType === FacilityModalAssignType.selectExperts
              ? 'CONFIRM'
              : 'ADD'}
          </Button>
        </>
      }
    >
      <h1 className="title">{assignType}</h1>
      <Search
        placeholder="Search"
        allowClear
        onSearch={onSearch}
        size="large"
      />
      {preFetching ? (
        <PageLoading />
      ) : (
        <Row gutter={[18, 18]} className="experts-list">
          {filteredPersons.map((item, index) => {
            return assignType === FacilityModalAssignType.selectExperts ? (
              <Col span={24} key={index}>
                <Checkbox
                  onChange={(e) => {
                    e.target.checked
                      ? handleSelectExpert(item)
                      : handleDeleteExpert(item.id)
                  }}
                  checked={selectedExperts.find(
                    (expert) => expert.id === item.id
                  )}
                >
                  <div className="avatar">
                    {item.avatar && item.avatar.oss_url && (
                      <img src={item.avatar.oss_url} alt="avatar" />
                    )}
                  </div>
                  <span className="name">{`${(item.profile &&
                    item.profile.nickname) ||
                    item.first_name} ${item.last_name}`}</span>
                </Checkbox>
                <span className="services">
                  {(_.get(item, 'services') || []).join(', ')}
                </span>
              </Col>
            ) : (
              <Col span={12} key={index}>
                <Checkbox
                  onChange={(e) => {
                    if (assignType === FacilityModalAssignType.addClient) {
                      e.target.checked
                        ? handleSelectClient(item)
                        : handleDeleteClient(item.id)
                    } else {
                      e.target.checked
                        ? handleSelectExpert(item)
                        : handleDeleteExpert(item.id)
                    }
                  }}
                  checked={
                    assignType === FacilityModalAssignType.addClient
                      ? selectedClients.find((client) => client.id === item.id)
                      : selectedExperts.find((expert) => expert.id === item.id)
                  }
                >
                  <div className="avatar">
                    {item.avatar && item.avatar.oss_url && (
                      <img src={item.avatar.oss_url} alt="avatar" />
                    )}
                  </div>
                  <span className="name">{`${(item.profile &&
                    item.profile.nickname) ||
                    item.first_name} ${item.last_name}`}</span>
                </Checkbox>
              </Col>
            )
          })}
        </Row>
      )}
    </StyledModal>
  )
}

export default FacilityAssignPersonModal
